import { GraphQLClient } from "../../../../../shared/gql-client";
import * as gqlBuilder from "gql-query-builder";
import { gql } from "@apollo/client";
import {
  RouteModel,
  RouteModelInput,
  ExportTruckLayoutModel,
} from "../../domain/models/routeModel";
import { routeSelection } from "../selections/routeSelection";

export default class RouteService {
  private client: GraphQLClient | undefined;

  setClient(client: GraphQLClient) {
    this.client = client;
  }

  static getRouteDelta(
    routes: RouteModel[],
    truckId?: string | undefined,
  ): number {
    const exitstingDeltas = routes
      .filter((route) => {
        if (truckId) {
          return route.truck?.id === truckId;
        }
        return route.truck == null;
      })
      .map((route) => route.delta);

    if (exitstingDeltas.length === 0) {
      return 0;
    }

    return Math.max(...exitstingDeltas) + 1;
  }

  generateTruckLayout(id: string): Promise<RouteModel> {
    return new Promise((resolve, reject) => {
      const { query, variables } = gqlBuilder.mutation({
        operation: "generateTruckLayout",
        variables: {
          route_id: { type: "ID!", value: id },
        },
        fields: ["id", "custom", "layout"],
      });

      this.client
        ?.mutate("generateTruckLayout", gql(query), variables)
        .then((response) => {
          resolve(response as RouteModel);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  exportTruckLayout(id: string): Promise<ExportTruckLayoutModel | null> {
    return new Promise((resolve, reject) => {
      const { query, variables } = gqlBuilder.query({
        operation: "exportTruckLayout",
        variables: {
          route_id: { type: "ID!", value: id },
        },
        fields: ["url", "filename", "mimeType"],
      });

      this.client
        ?.query("exportTruckLayout", gql(query), variables)
        .then((response) => {
          const data = response as ExportTruckLayoutModel;
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getRoutesFromDay(day: string): Promise<RouteModel[]> {
    return new Promise((resolve, reject) => {
      const { query, variables } = gqlBuilder.query({
        operation: "routes",
        variables: {
          where: {
            type: "SearchByRootRoute",
            value: {
              field: {
                date: {
                  equal: day,
                },
              },
            },
          },
          first: 1000,
        },
        fields: [
          {
            data: routeSelection,
          },
        ],
      });

      this.client
        ?.query("routes", gql(query), variables)
        .then((response) => {
          const data = response as { data: RouteModel[] };
          if (!data.data || data.data.length === 0) {
            resolve([]);
          }

          resolve(data.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  connectRequestsToRoute(
    requestIds: string[],
    route: RouteModel,
    pickup_amount: number,
    weight?: number,
    relation_id?: number,
  ): Promise<RouteModel | undefined> {
    const vars = route.id.startsWith("state:")
      ? {
          request_ids: { type: "[ID!]!", value: requestIds },
          truck_id: { type: "ID", value: route?.truck?.id },
          date: { type: "Date", value: route.date },
          delta: { type: "Int", value: route.delta },
          weight: { type: "Int", value: weight },
          pickup_amount: { type: "Int", value: pickup_amount },
          relation_id: { type: "ID", value: relation_id },
        }
      : {
          request_ids: { type: "[ID!]!", value: requestIds },
          route_id: { type: "ID", value: route.id },
          weight: { type: "Int", value: weight },
          pickup_amount: { type: "Int", value: pickup_amount },
          relation_id: { type: "ID", value: relation_id },
        };

    return new Promise((resolve, reject) => {
      const { query, variables } = gqlBuilder.mutation({
        operation: "connectRequestsToRoute",
        variables: vars,
        fields: routeSelection,
      });

      this.client
        ?.mutate("connectRequestsToRoute", gql(query), variables)
        .then((response) => {
          const data = response as RouteModel;

          if (data.id === null) {
            resolve(undefined);
          }

          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  connectPackingSlipToRoute(
    route_id: string,
    packing_slip_id: string,
    weight?: number,
  ): Promise<RouteModel | undefined> {
    const vars = {
      route_id: { type: "ID!", value: route_id },
      packing_slip_id: { type: "ID!", value: packing_slip_id },
      weight: { type: "Int", value: weight },
    };

    return new Promise((resolve, reject) => {
      const { query, variables } = gqlBuilder.mutation({
        operation: "connectPackingSlipToRoute",
        variables: vars,
        fields: routeSelection,
      });

      this.client
        ?.mutate("connectPackingSlipToRoute", gql(query), variables)
        .then((response) => {
          const data = response as RouteModel;

          if (data.id === null) {
            resolve(undefined);
          }

          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createRoute(route: RouteModelInput): Promise<RouteModel | undefined> {
    return new Promise((resolve, reject) => {
      const { query, variables } = gqlBuilder.mutation({
        operation: "upsertRoute",
        variables: {
          date: { type: "Date", value: route.date },
          delta: { type: "Int", value: route.delta },
        },
        fields: routeSelection,
      });

      this.client
        ?.mutate("upsertRoute", gql(query), variables)
        .then((response) => {
          resolve(response as RouteModel);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  updateRouteTruck(
    routeId: string,
    truckId: string,
    delta: number,
  ): Promise<RouteModel | null> {
    return new Promise((resolve, reject) => {
      const { query, variables } = gqlBuilder.mutation({
        operation: "upsertRoute",
        variables: {
          id: { type: "ID!", value: routeId },
          truck_id: { type: "ID!", value: truckId },
          delta: { type: "Int", value: delta },
        },
        fields: routeSelection,
      });

      this.client
        ?.mutate("upsertRoute", gql(query), variables)
        .then((response) => {
          resolve(response as RouteModel);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getRouteFromData(route: RouteModel): Promise<string | null> {
    return new Promise((resolve, reject) => {
      const { query, variables } = gqlBuilder.query({
        operation: "routes",
        variables: {
          where: {
            type: "SearchByRootRoute",
            value: {
              allOf: [
                {
                  field: {
                    date: {
                      equal: route.date,
                    },
                  },
                },
                {
                  field: {
                    delta: {
                      equal: route.delta,
                    },
                  },
                },
                {
                  field: {
                    truck: {
                      where: {
                        field: {
                          id: {
                            equal: route?.truck?.id,
                          },
                        },
                      },
                    },
                  },
                },
              ],
            },
          },
          first: 1,
        },
        fields: [
          {
            data: ["id"],
          },
        ],
      });

      this.client
        ?.query("routes", gql(query), variables)
        .then((response) => {
          const data = response as { data: RouteModel[] };

          if (data.data.length <= 0) {
            resolve(null);
          }

          resolve(data.data[0].id);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  delete(id: string): Promise<RouteModel | null> {
    return new Promise((resolve, reject) => {
      const { query, variables } = gqlBuilder.mutation({
        operation: "deleteRoute",
        variables: {
          id: {
            name: "id",
            type: "ID!",
            value: id,
          },
        },
        fields: ["id"],
      });

      this.client
        ?.mutate("deleteRoute", gql(query), variables)
        .then((response) => {
          const data = response as RouteModel;

          if (!data.id) {
            resolve(null);
          }

          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  update(input: object): Promise<RouteModel | null> {
    return new Promise((resolve, reject) => {
      const { query, variables } = gqlBuilder.mutation({
        operation: "upsertRoute",
        variables: input,
        fields: ["id"],
      });

      this.client
        ?.mutate("upsertRoute", gql(query), variables)
        .then((response) => {
          const data = response as RouteModel;

          if (!data.id) {
            resolve(null);
          }

          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateLayout(input: object): Promise<RouteModel | null> {
    return new Promise((resolve, reject) => {
      const { query, variables } = gqlBuilder.mutation({
        operation: "upsertRoute",
        variables: input,
        fields: [
          "id",
          "layout",
          {
            truck: ["id"],
          },
        ],
      });

      this.client
        ?.mutate("upsertRoute", gql(query), variables)
        .then((response) => {
          const data = response as RouteModel;

          if (!data.id) {
            resolve(null);
          }

          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateRoutesDefinitive(routeIds: string[], status: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const { query, variables } = gqlBuilder.mutation({
        operation: "updateRoutesDefinitive",
        variables: {
          route_ids: { type: "[ID!]!", value: routeIds },
          status: { type: "String", value: status },
        },
      });

      this.client
        ?.mutate("updateRoutesDefinitive", gql(query), variables)
        .then((response) => {
          resolve(response as boolean);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  generateRoutesFromDate(date: string): Promise<number> {
    return new Promise((resolve, reject) => {
      const { query, variables } = gqlBuilder.mutation({
        operation: "generateRoutesFromDate",
        variables: { date: { type: "Date!", value: date } },
      });

      this.client
        ?.mutate("generateRoutesFromDate", gql(query), variables)
        .then((response) => {
          resolve(response as number);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }
}
